import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useArchiveExternalProvider,
  useCreateExternalProvider,
  useExternalProviders,
  useUpdateExternalProvider,
} from "@/hooks/useExternalProviders";
import { useCategoryByType } from "@/hooks/useCategories";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import {
  ExternalProvider,
  ExternalProviderPayload,
} from "@/domain/externalProviders.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import ExternalProviderFilters, {
  Filters,
} from "@/ui/content/externalProviders/Filters";
import ExternalProviderModal from "@/ui/modals/externalProviders/ExternalProviderModal";

const EXTERNAL_PROVIDER_CATEGORY_TYPE = "EXTERNAL_PROVIDERS";

const DEFAULT_FILTERS: Filters = {
  sortField: "name",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
  categoryId: "",
};

const pickCategoryLabel = (
  langs: { name: string; language: string }[] | undefined
) => {
  if (!langs || langs.length === 0) return "";
  const en = langs.find((l) => l.language?.toLowerCase().startsWith("en"));
  return (en || langs[0]).name;
};

const ExternalProvidersPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    externalProvider?: ExternalProvider;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    externalProvider?: ExternalProvider;
  }>({ open: false });

  const {
    externalProviders,
    isLoadingExternalProviders,
    isFetchingExternalProviders,
    refetchExternalProviders,
  } = useExternalProviders({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    status: filters.status,
    categoryId: filters.categoryId,
  });

  const createProvider = useCreateExternalProvider();
  const updateProvider = useUpdateExternalProvider();
  const archiveProvider = useArchiveExternalProvider();
  const createAuditLog = useCreateAuditlog();

  const { category } = useCategoryByType({
    type: EXTERNAL_PROVIDER_CATEGORY_TYPE,
    status: "ACTIVE",
  });

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    (category?.categories || []).forEach((c) => {
      if (c._id) map.set(c._id, pickCategoryLabel(c.languages) || c._id);
    });
    return map;
  }, [category]);

  const isSaving = createProvider.isPending || updateProvider.isPending;

  useEffect(() => {
    setPageTitle("External Providers");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.EXTERNAL_PROVIDERS,
    option: OPTIONS.EXTERNAL_PROVIDERS_LIST,
    actionCode: ACTION_CODE.WEB_EXT_PROV_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of external providers",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchExternalProviders();
  }, [
    debouncedSearch,
    filters.status,
    filters.categoryId,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.EXTERNAL_PROVIDERS,
        option: OPTIONS.EXTERNAL_PROVIDERS_LIST,
        actionCode: ACTION_CODE.WEB_EXT_PROV_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of external providers",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, externalProvider: undefined });
  };

  const handleOpenEdit = (externalProvider: ExternalProvider) => {
    setModalState({ open: true, externalProvider });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, externalProvider: undefined });
  };

  const handleSave = async (data: ExternalProviderPayload) => {
    const editing = modalState.externalProvider;

    try {
      if (editing) {
        await updateProvider.mutateAsync({ id: editing._id, payload: data });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.EXTERNAL_PROVIDERS,
          option: OPTIONS.EXTERNAL_PROVIDER_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_EXT_PROV_EDIT,
          action: "When editing an external provider",
          detail: `Edited external provider ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The external provider has been updated.", "success");
      } else {
        await createProvider.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.EXTERNAL_PROVIDERS,
          option: OPTIONS.EXTERNAL_PROVIDER_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_EXT_PROV_ADD,
          action: "When creating an external provider",
          detail: `Created external provider ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The external provider has been created.", "success");
      }

      handleCloseModal();
      refetchExternalProviders();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.externalProvider;
    if (!target) return;

    try {
      await archiveProvider.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.EXTERNAL_PROVIDERS,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_EXT_PROV_DELETE,
        action: "When deleting an external provider",
        detail: `Deleted external provider ${target.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchExternalProviders();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "200px",
    },
    {
      id: "category",
      isSortable: false,
      disablePadding: false,
      label: "Category",
      align: "left",
      width: "160px",
    },
    {
      id: "email",
      isSortable: false,
      disablePadding: false,
      label: "Email",
      align: "left",
      width: "200px",
    },
    {
      id: "phoneNumber",
      isSortable: false,
      disablePadding: false,
      label: "Phone",
      align: "left",
      width: "140px",
    },
    {
      id: "languages",
      isSortable: false,
      disablePadding: false,
      label: "Languages",
      align: "left",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "120px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: " ",
      align: "right",
      width: "80px",
    },
  ];

  const rows = useMemo(() => {
    if (!externalProviders || externalProviders.list.length === 0) return [];

    return externalProviders.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, externalProvider: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const languageCodes = (row.languages || [])
        .map((l) => l.language)
        .filter(Boolean);

      return createDataRow(index, {
        name: <div>{row.name}</div>,
        category: (
          <div>
            {categoryNameById.get(row.categoryId) || row.categoryId || "-"}
          </div>
        ),
        email: <div>{row.email || "-"}</div>,
        phoneNumber: <div>{row.phoneNumber || "-"}</div>,
        languages:
          languageCodes.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {languageCodes.map((code, i) => (
                <Chip
                  key={`${row._id}-lang-${i}`}
                  size="small"
                  label={<div className="text-xs">{code}</div>}
                />
              ))}
            </Stack>
          ) : (
            <div>-</div>
          ),
        status: row.status ? (
          <Chip
            sx={{
              bgcolor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(row.status)}</div>}
          />
        ) : (
          <div>-</div>
        ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [externalProviders, theme, categoryNameById]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    External Providers
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <ExternalProviderFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={externalProviders?.totalCount}
                  isLoading={
                    isLoadingExternalProviders || isFetchingExternalProviders
                  }
                  messageEmptyData="No external providers to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <ExternalProviderModal
          open={modalState.open}
          onClose={handleCloseModal}
          externalProvider={modalState.externalProvider}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveProvider.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default ExternalProvidersPage;
